import { createI18n } from 'vue-i18n';

import en from './locales/en.json';
import ru from './locales/ru.json';
import es from './locales/es.json';
import cs from './locales/cs.json';

export const SUPPORTED_LOCALES = ['en', 'ru', 'es', 'cs'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';
const STORAGE_KEY = 'avp.locale.v1';

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  ru: 'Русский',
  es: 'Español',
  cs: 'Čeština',
};

/** Map UI locale code → BCP-47 tag for `toLocaleString` / `toLocaleDateString`. */
export function toBcp47Locale(locale: string): string {
  switch (locale) {
    case 'ru':
      return 'ru-RU';
    case 'es':
      return 'es-ES';
    case 'cs':
      return 'cs-CZ';
    default:
      return 'en-US';
  }
}

export function isSupportedLocale(value: string | null | undefined): value is SupportedLocale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

function detectInitialLocale(): SupportedLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isSupportedLocale(stored)) return stored;
  } catch {
    // localStorage unavailable — fall through to navigator detection
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language?.slice(0, 2).toLowerCase() : '';
  if (isSupportedLocale(nav)) return nav;
  return DEFAULT_LOCALE;
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: detectInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: { en, ru, es, cs },
});

export function setLocale(locale: SupportedLocale): void {
  i18n.global.locale.value = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore storage errors (private mode, etc.)
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}
